// @file Vuex store Module for Section
/* eslint-disable no-param-reassign */
import { captureFetchException } from 'bits/error_tracker'
import sectionSorter from 'bits/section_sorter'
import PadletApi from 'surface/padlet_api'
import { scrollToSectionId } from 'surface/dom_helpers'
import { Id, Section } from 'types'
import Vue from 'vue'

export default {
  namespaced: true,
  state: {
    sectionsFetched: false,
    sectionEntities: {},
    pendingSectionId: null,
    sectionBeingEditedId: null,
    actionMenuSectionId: null,
  },
  getters: {
    sections: (state, getters): Section[] => getters.sectionIds.map(id => state.sectionEntities[id]),
    sortedSections: (_, getters): Section[] => sectionSorter.sortSections(getters.sections),
    sectionIds: (state): string[] => Object.keys(state.sectionEntities),
    numOfSections: (_, getters): number => getters.sectionIds.length,
    getArrayIndex: (_, getters) => (sectionId: Id): number =>
      getters.sortedSections.findIndex((section: Section) => section.id === sectionId),
  },
  mutations: {
    FETCH_SECTIONS_SUCCESS(state, sections): void {
      const restorePendingSection = (newSectionEntities): void => {
        const pendingSection = state.sectionEntities[state.pendingSectionId]
        if (pendingSection) {
          newSectionEntities[state.pendingSectionId] = pendingSection
        }
      }

      const restoreEditedTitle = (newSectionEntities): void => {
        const sectionBeingEdited = state.sectionEntities[state.sectionBeingEditedId]
        const incomingSection = newSectionEntities[state.sectionBeingEditedId]
        if (sectionBeingEdited && incomingSection && sectionBeingEdited.title !== incomingSection.title) {
          incomingSection.title = sectionBeingEdited.title
        }
      }

      if (sections) {
        const newSectionEntities = {}
        sections.forEach(section => {
          newSectionEntities[section.id] = section
        })
        restorePendingSection(newSectionEntities)
        restoreEditedTitle(newSectionEntities)
        state.sectionEntities = newSectionEntities // Trigger state change only once
      }
      state.sectionsFetched = true
    },
    ADD_PENDING_SECTION(state, pendingSection): void {
      const tempId = 'new_' + new Date().valueOf()
      state.sectionEntities = {
        ...state.sectionEntities,
        [tempId]: {
          ...pendingSection,
          id: tempId,
        },
      }
      state.pendingSectionId = tempId
    },
    ADD_SECTION(state, section): void {
      const newSectionEntities = { ...state.sectionEntities }
      // delete pending section
      delete newSectionEntities[state.pendingSectionId]
      // put new section in place of pending section
      state.sectionEntities = {
        ...newSectionEntities,
        [section.id]: section,
      }
      state.pendingSectionId = null
    },
    UPDATE_SECTION(state, { id, obj }): void {
      // Vue.set(state.sectionEntities[sectionId], 'title', title)
      Vue.set(state.sectionEntities, id, obj)
    },
    REMOVE_SECTION(state, id): void {
      if (state.pendingSectionId === id) {
        state.pendingSectionId = null
      }
      const newSectionEntities = { ...state.sectionEntities }
      delete newSectionEntities[id]
      state.sectionEntities = newSectionEntities
    },
    SHOW_SECTION_ACTION_MENU(state, sectionId): void {
      state.actionMenuSectionId = sectionId
    },
    HIDE_SECTION_ACTION_MENU(state): void {
      state.actionMenuSectionId = null
    },
    START_EDITING_SECTION(state, sectionId): void {
      state.sectionBeingEditedId = sectionId
    },
    STOP_EDITING_SECTION(state): void {
      state.sectionBeingEditedId = null
    },
  },
  actions: {
    fetchSections({ dispatch, rootState }): void {
      PadletApi.WallSection.readAll({ wall: { id: rootState.wall.id } })
        .then(sections => {
          dispatch('fetchSectionsSuccess', { sections })
        })
        .catch(e => {
          captureFetchException(e, { source: 'fetchSections' })
        })
    },
    fetchSectionsSuccess({ commit }, { sections }): void {
      commit('FETCH_SECTIONS_SUCCESS', sections)
    },

    /* ------------
     * PANEL
     * ------------ */
    showSectionActionMenu({ commit }, { sectionId }): void {
      commit('SHOW_SECTION_ACTION_MENU', sectionId)
    },
    hideSectionActionMenu({ commit }): void {
      commit('HIDE_SECTION_ACTION_MENU')
    },

    /* ------------
     * INTERACTIONS
     * ------------ */
    deleteSection({ commit, state }, { sectionId }): void {
      const existingSection = state.sectionEntities[sectionId]
      commit('STOP_EDITING_SECTION')
      if (existingSection) {
        commit('REMOVE_SECTION', sectionId)
        PadletApi.WallSection.delete(existingSection)
      }
      // oldStore.dispatch({ type: 'DELETE_SECTION', sectionId })
    },
    startEditingSection({ commit, dispatch }, { sectionId }): void {
      dispatch('stopEditingSection')
      commit('START_EDITING_SECTION', sectionId)
    },
    stopEditingSection({ commit, state }): void {
      if (state.sectionBeingEditedId && String(state.sectionBeingEditedId).startsWith('new_')) {
        commit('REMOVE_SECTION', state.sectionBeingEditedId)
      }
      commit('STOP_EDITING_SECTION')
    },
    startWithNoSections({ dispatch, getters }): void {
      if (getters.numOfSections === 0) {
        dispatch('startNewSection', { section: { sort_index: sectionSorter.getBottomSortIndex() } })
      }
    },
    startNewSection({ state, commit, dispatch }, { section }): void {
      dispatch('stopEditingSection')
      commit('ADD_PENDING_SECTION', section)
      commit('START_EDITING_SECTION', state.pendingSectionId)
    },
    changeSectionTitle({ commit, state, rootState }, { sectionId, title }): void {
      if (state.sectionEntities[sectionId].title !== title) {
        commit('UPDATE_SECTION', {
          id: sectionId,
          obj: {
            ...state.sectionEntities[sectionId],
            title: title,
          },
        })
      }
      if (state.pendingSectionId) {
        // new section being edited
        PadletApi.WallSection.create({
          ...state.sectionEntities[state.pendingSectionId],
          wall_id: rootState.wall.id,
          title: title,
        }).then(section => {
          commit('ADD_SECTION', section)
        })
      } else {
        PadletApi.WallSection.update({ ...state.sectionEntities[sectionId], title })
      }
      if (state.sectionBeingEditedId === sectionId) {
        commit('STOP_EDITING_SECTION')
      }
    },
    sortedSection({ commit, state }, { sectionId, sortIndex, gapSize }): void {
      // isStuck is just a safeguard. In the expected flow, isStuck should not be the one to trigger the network call.
      // Any corrective action should have been done in a previous request.
      const sameIndex = state.sectionEntities[sectionId].sortIndex === sortIndex
      const isStuck = gapSize <= 4
      if (sameIndex && !isStuck) return

      const updatedObject = { ...state.sectionEntities[sectionId], sort_index: sortIndex }
      commit('UPDATE_SECTION', { id: sectionId, obj: updatedObject })
      PadletApi.WallSection.update({ ...updatedObject, gap_size: gapSize })
    },
    moveSectionLeft({ dispatch, getters }, { sectionId }): void {
      const index = getters.getArrayIndex(sectionId)

      // No-op if already leftmost
      if (index <= 0) return

      const section = getters.sortedSections[index]
      let sortIndex = section.sort_index
      let gapSize: number | null = null

      if (index === 1) {
        sortIndex = sectionSorter.getTopSortIndex()
      } else {
        const leftSection = getters.sortedSections[index - 2]
        const rightSection = getters.sortedSections[index - 1]
        sortIndex = (rightSection.sort_index + leftSection.sort_index) / 2
        gapSize = sortIndex - leftSection.sort_index
      }

      dispatch('sortedSection', { sectionId, sortIndex, gapSize })
      Vue.nextTick(() => scrollToSectionId(sectionId, { smooth: false, centralize: true }))
    },
    moveSectionRight({ dispatch, getters }, { sectionId }): void {
      const index = getters.getArrayIndex(sectionId)

      // No-op if already rightmost
      if (index < 0 || index >= getters.numOfSections - 1) return

      const section = getters.sortedSections[index]
      let sortIndex = section.sort_index
      let gapSize: number | null = null

      if (index === getters.numOfSections - 2) {
        sortIndex = sectionSorter.getBottomSortIndex()
      } else {
        const leftSection = getters.sortedSections[index + 1]
        const rightSection = getters.sortedSections[index + 2]
        sortIndex = (rightSection.sort_index + leftSection.sort_index) / 2
        gapSize = sortIndex - leftSection.sort_index
      }

      dispatch('sortedSection', { sectionId, sortIndex, gapSize })
      Vue.nextTick(() => scrollToSectionId(sectionId, { smooth: false, centralize: true }))
    },
    changeSectionColor(): void {
      // TODO:
    },
    removeSectionColor(): void {
      // TODO:
    },

    /* ------------
     * REMOTE
     * ------------ */
    newSectionRemote({ commit }, section): void {
      commit('ADD_SECTION', section)
    },
    editSectionRemote({ commit }, section): void {
      commit('UPDATE_SECTION', { id: section.id, obj: section })
    },
    deleteSectionRemote({ commit }, section): void {
      commit('REMOVE_SECTION', section.id)
    },
  },
}
